import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { hasValue } from '@utils';
import Select from './Select';
/**
 * A two-level cascading select. Picking a value in the primary select (re)loads the
 * secondary select's options — App → Role, Country → City, Site → Location and the like.
 *
 * Both levels support the full Select data contract: a static array, or paginated async
 * loading with search, debounce and infinite scroll. The component is fully controlled:
 * pass `selectedPrimary` / `selectedSecondary` and update them in `onChange`, which fires
 * `(p, '')` on a primary pick, `(p, s)` when the pair completes, and `('', '')` on clear.
 * Data functions may be defined inline — re-renders never reload the secondary; only a
 * real primary change does.
 *
 * @example
 *
 * ```
 * // Static primary, service-backed paginated secondary. The loader receives the
 * // selected primary directly, plus args.query (the debounced search text).
 * const [app, setApp] = useState('');
 * const [role, setRole] = useState('');
 *
 * <CascadingSelect
 *     primary={{ options: [{ label: 'System', value: 'System' }, { label: 'Lucy', value: 'Lucy' }], placeholder: 'App' }}
 *     secondary={{
 *         loadOptions: async (max, last, app, _appOption, args) => {
 *             const res = await context.executeService('MyApp', 'GetRoles',
 *                 { app, q: args?.query || '', max, last }, { json: true });
 *             return { items: res.items, pageToken: res.pageToken };
 *         },
 *         placeholder: 'Role',
 *     }}
 *     selectedPrimary={app}
 *     selectedSecondary={role}
 *     onChange={(p, s) => { setApp(p); setRole(s); }}
 * />
 * ```
 *
 * @example
 *
 * ```
 * // Static secondary as a function of the primary — no service needed.
 * <CascadingSelect
 *     primary={{ options: COUNTRIES, placeholder: 'Country' }}
 *     secondary={{ options: (country) => CITIES[country] || [], placeholder: 'City' }}
 *     selectedPrimary={country}
 *     selectedSecondary={city}
 *     onChange={(p, s) => { setCountry(p); setCity(s); }}
 * />
 * ```
 *
 * @example
 *
 * ```
 * // Hydrated saved values: pass selectedLabel per level so the triggers can show
 * // labels for values that were saved earlier — loaded pages may not contain them.
 * // (Values the user picks in-session display without this.)
 * <CascadingSelect
 *     primary={{
 *         options: loadApps,
 *         selectedLabel: (app) => context.executeService('MyApp', 'GetAppDetails', { app }, { json: true }),
 *     }}
 *     secondary={{
 *         loadOptions: loadRoles,
 *         selectedLabel: (role) => context.executeService('MyApp', 'GetRoleDetails', { role }, { json: true }),
 *     }}
 *     selectedPrimary={savedApp}
 *     selectedSecondary={savedRole}
 *     onChange={(p, s) => { setSavedApp(p); setSavedRole(s); }}
 * />
 * ```
 * @export
 *
 * @category Inputs/Selection
 */
const CascadingSelect = (props) => {
    var _a;
    const { primary, secondary, selectedPrimary, selectedSecondary, onChange, className, fullWidth, unified, disabled, readOnly, isValid, } = props;
    // Last-known primary option object — echoed back through onChange, and handed to
    // the secondary data source.
    const primaryOptionRef = useRef(undefined);
    // Latest-ref pattern: the consumer's data source is always read through a ref, so
    // its identity never reaches the memo dependencies. A parent re-render can never
    // restart the secondary list — only a real primary-value change does.
    const loadRef = useRef(secondary.loadOptions);
    loadRef.current = secondary.loadOptions;
    const staticRef = useRef(secondary.options);
    staticRef.current = secondary.options;
    // Warn once per mount, not once per render.
    const warnedBothRef = useRef(false);
    if (!warnedBothRef.current && secondary.loadOptions && secondary.options) {
        warnedBothRef.current = true;
        console.warn('CascadingSelect: `secondary` was given both `options` and `loadOptions`. `loadOptions` wins — provide exactly one.');
    }
    // The cascade. Both hooks run unconditionally (Rules of Hooks) and the value handed
    // to the secondary Select is picked afterwards. Each depends on `selectedPrimary`
    // alone: its identity changes only with the primary, which makes Select rebuild its
    // loader and DataList reset to page 0 — a fresh load, no Select changes needed.
    const paginatedOptions = useCallback((max, lastPageToken, args) => loadRef.current(max, lastPageToken, selectedPrimary !== null && selectedPrimary !== void 0 ? selectedPrimary : '', primaryOptionRef.current, args), [selectedPrimary]);
    const computedStaticOptions = useMemo(() => typeof staticRef.current === 'function'
        ? staticRef.current(selectedPrimary !== null && selectedPrimary !== void 0 ? selectedPrimary : '', primaryOptionRef.current)
        : undefined, [selectedPrimary]);
    const secondaryOptions = secondary.loadOptions
        ? paginatedOptions
        : typeof secondary.options === 'function'
            ? (computedStaticOptions !== null && computedStaticOptions !== void 0 ? computedStaticOptions : [])
            : ((_a = secondary.options) !== null && _a !== void 0 ? _a : []);
    const handlePrimaryChange = (value, option) => {
        primaryOptionRef.current = option;
        // a primary change always clears the secondary selection
        onChange(value, '', option, undefined);
    };
    const handleSecondaryChange = (value, option) => {
        onChange(selectedPrimary !== null && selectedPrimary !== void 0 ? selectedPrimary : '', value, primaryOptionRef.current, option);
    };
    return (_jsxs("div", { className: classNames('uxpcore_cascadingselect', { 'uxpcore_cascadingselect--full': fullWidth }, { 'uxpcore_cascadingselect--unified': unified }, className), children: [_jsx(Select, { options: primary.options, labelField: primary.labelField, valueField: primary.valueField, iconField: primary.iconField, selected: selectedPrimary !== null && selectedPrimary !== void 0 ? selectedPrimary : '', selectedLabel: primary.selectedLabel, onChange: handlePrimaryChange, onClear: () => {
                    primaryOptionRef.current = undefined;
                    onChange('', '', undefined, undefined);
                }, placeholder: primary.placeholder, renderOption: primary.renderOption, pageSize: primary.pageSize, dropdownMinWidth: primary.dropdownMinWidth, dropdownMaxWidth: primary.dropdownMaxWidth, showEndOfContent: primary.showEndOfContent, disabled: disabled, readOnly: readOnly, isValid: isValid, fullWidth: fullWidth }), _jsx(Select
            // Remount on primary change: discards the old list's in-flight fetches
            // (useDataList has no cancellation — last resolver would win), and resets
            // the secondary's internal search/option cache. Cheap — dropdown content
            // only mounts when opened, so no fetch fires until the user opens it.
            , { options: secondaryOptions, labelField: secondary.labelField, valueField: secondary.valueField, iconField: secondary.iconField, selected: selectedSecondary !== null && selectedSecondary !== void 0 ? selectedSecondary : '', selectedLabel: secondary.selectedLabel, onChange: handleSecondaryChange, onClear: () => onChange(selectedPrimary !== null && selectedPrimary !== void 0 ? selectedPrimary : '', '', primaryOptionRef.current, undefined), placeholder: secondary.placeholder, renderOption: secondary.renderOption, pageSize: secondary.pageSize, dropdownMinWidth: secondary.dropdownMinWidth, dropdownMaxWidth: secondary.dropdownMaxWidth, showEndOfContent: secondary.showEndOfContent, disabled: disabled || !hasValue(selectedPrimary), readOnly: readOnly, isValid: isValid, fullWidth: fullWidth }, `sec-${selectedPrimary !== null && selectedPrimary !== void 0 ? selectedPrimary : ''}`)] }));
};
export default CascadingSelect;
